import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { firstValueFrom, BehaviorSubject } from 'rxjs';
import { environment } from '../../environment/environment';
import { initializeApp, deleteApp, getApps } from 'firebase/app';
import { AppConfig } from '../models/config.interface';
import { DeviceInfo } from '../models/auth.interface';
import { authConfig } from '../templates/auth/authConfig';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private http = inject(HttpClient);
  private apiService = inject(ApiService);
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);
  private configSource = new BehaviorSubject<AppConfig | null>(null);
  config$ = this.configSource.asObservable();

  async loadConfig(): Promise<void> {
    if (!this.isBrowser) {
      return;
    }

    try {
      const token = this.isBrowser ? localStorage.getItem('token') : null;
      const headers: Record<string, string> = token ? { 'x-access-token': token } : {};

      // API call to fetch encrypted config
      const response = await firstValueFrom(
        this.http.get<{
          success: boolean;
          data: { encryptedConfig: string };
        }>(`${environment.apiUrl}api/v1/api-gateway/app-config/encrypted`, { headers })
      );

      if (response && response.success && response.data?.encryptedConfig) {
        const decryptedConfig = await this.decrypt(response.data.encryptedConfig);
        if (decryptedConfig) {
          await this.applyConfig(decryptedConfig);

          // Update device after config is applied
          const deviceToken = localStorage.getItem('deviceToken');
          if (deviceToken) {
            this.updateDevice(deviceToken).subscribe();
          }
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('ConfigService: Error loading config:', error);
    }
  }

  public updateDevice(deviceToken: string) {
    const deviceInfo = this.getDeviceInfo();
    const payload = {
      deviceToken,
      device_info: JSON.stringify(deviceInfo),
    };
    return this.apiService.patch(authConfig.updateDevice, payload);
  }

  public getDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent || '';
    const platformRaw = navigator.platform || '';
    const language = navigator.language || '';
    const ua = userAgent.toLowerCase();

    let deviceType = 'web';
    if (/mobile|iphone|ipod|android.*mobile|windows phone/i.test(ua)) {
      deviceType = 'mobile';
    } else if (/tablet|ipad|android(?!.*mobile)/i.test(ua)) {
      deviceType = 'tablet';
    }

    let platform: 'android' | 'ios' | 'web' | null;
    if (/android/i.test(ua)) {
      platform = 'android';
    } else if (/iphone|ipad|ipod/i.test(ua)) {
      platform = 'ios';
    } else if (/win|mac|linux/i.test(platformRaw.toLowerCase())) {
      platform = 'web';
    } else {
      platform = 'web';
    }

    return {
      user_agent: userAgent,
      platform,
      language,
      device_type: deviceType,
    };
  }

  private async decrypt(encryptedConfigB64: string): Promise<AppConfig | null> {
    try {
      const encryptedConfig = JSON.parse(atob(encryptedConfigB64));
      const { iv, authTag, encryptedData } = encryptedConfig;
      const keyStr = 'wareed_44636344';

      const encoder = new TextEncoder();
      const keyData = encoder.encode(keyStr);
      const hash = await crypto.subtle.digest('SHA-256', keyData);

      const cryptoKey = await crypto.subtle.importKey('raw', hash, { name: 'AES-GCM' }, false, [
        'decrypt',
      ]);

      const ivBuffer = Uint8Array.from(atob(iv), (c) => c.charCodeAt(0));
      const tagBuffer = Uint8Array.from(atob(authTag), (c) => c.charCodeAt(0));
      const dataBuffer = Uint8Array.from(atob(encryptedData), (c) => c.charCodeAt(0));

      const combined = new Uint8Array(dataBuffer.length + tagBuffer.length);
      combined.set(dataBuffer);
      combined.set(tagBuffer, dataBuffer.length);

      const decryptedBuffer = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: ivBuffer, tagLength: 128 },
        cryptoKey,
        combined
      );

      return JSON.parse(new TextDecoder().decode(decryptedBuffer));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('ConfigService: Decryption failed:', error);
      return null;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async applyConfig(config: any): Promise<void> {
    if (config.googleMapsKey) {
      environment.googleMapsKey = config.googleMapsKey;
    }
    if (config.xAccessToken) {
      environment.xAccessToken = config.xAccessToken;
    }
    if (config.firebase) {
      // Merge firebase config to preserve existing structure if any
      environment.firebase = {
        ...environment.firebase,
        ...config.firebase,
      };

      // Ensure Firebase is initialized with the new credentials
      if (this.isBrowser) {
        try {
          const apps = getApps();
          if (apps.length > 0) {
            await deleteApp(apps[0]);
            initializeApp(environment.firebase);
          } else {
            initializeApp(environment.firebase);
          }
        } catch {
          initializeApp(environment.firebase);
        }
      }
    }
    // eslint-disable-next-line no-console
    console.log('ConfigService: Dynamic configuration applied successfully.');
    this.configSource.next(config);
  }
}
